import { isNumber } from 'lodash-es';
// Useful resources:
//
// Work Breaks v1 Specification - Rules & Enforcement:
// https://docs.google.com/document/d/1YPyV3YmQSdT06dBHG3HnPHEXAjBw0tj1YA2zJi9PGSM/edit#heading=h.7umkzxjdt0jp
//
// Work break supported rules and scenarios:
// https://docs.google.com/document/d/1aqwIYPjA90cha8lsLp7DK7ZTvVIIktnplryzN3zXohc/edit#heading=h.w4zrwhy2yf1m

// Return a filtered sorted copy of the blocks.
// We need this to ensure block_indexes are in the right order and that they are from the same policy
// as the algorithm works based on the last and next to last block.
const filteredSortedBlocks = (session, policy) =>
  [...session.work_session_blocks]
    .filter((block) => block.work_break_policy_id === policy.id)
    .sort((a, b) => a.block_index - b.block_index);
const hasTakenAnyBreak = (session) =>
  session.work_session_blocks && session.work_session_blocks.some((block) => block.break_taken);
// If the time tracked for the current block is 100% earn_req_per_block, we're only going to be able
// to take a break right after the next block begins, but that "next block" wasn't created yet.
// "Impossible to happen" because we can't take a break we just earned without tracking a new block first;
// in the UI, the "Take a break" button is only available after beginning time tracking. We're testing it
// just to be sure.
const isValidBlock = (session, policy) => {
  const blocks = filteredSortedBlocks(session, policy);
  const current = blocks.pop();

  return !!current && current.tracked && current.tracked !== policy.earn_req_per_block;
};
// Session time requirements: the session must at least match the policy's earn_req_per_session.
const meetsMinSessionTime = (session, policy) =>
  isNumber(session.tracked) && isNumber(policy.earn_req_per_session) && session.tracked >= policy.earn_req_per_session;
// Block requirements (rules 4 and 5):
// Rule 4: we only need to check if a break has been taken on this block.
// Rule 5:
// If policy.earn_req_per_session >= policy.block_duration:
// 1. Can't take a break during the first block - must have a "previous" block (because of 2.)
// 2. Must have fulfilled the policy's block_duration in the previous block (can only take a break after this)
// 3. Did not take a break in the current block yet
//
// If policy.earn_req_per_session < policy.block_duration:
// we only need to evaluate if the current block's break has been taken.
const meetsWithinBlockRequirements = (session, policy) => {
  const blocks = filteredSortedBlocks(session, policy);
  const current = blocks.pop();
  const previous = blocks.pop();

  if (policy.earn_req_per_session && session.tracked >= policy.earn_req_per_session) {
    if (policy.earn_req_per_session >= policy.block_duration) {
      return !!previous && !!current && previous.tracked === policy.block_duration && !current.break_taken;
    }
  }

  return !!current && !current.break_taken;
};
// Block requirements (rules 6 and 7):
//
// 1. Must have a valid "previous block" to check if we can take a break
// 2. Must have a valid "current block"
// 3. Must have earned a break (matched because previous block_duration = earn_req_per_block)
// 4. Must not have taken a break in the current block
const meetsAccummulatingBlockRequirements = (session, policy) => {
  const blocks = filteredSortedBlocks(session, policy);
  const current = blocks.pop();
  const previous = blocks.pop();
  const earnedBlock = current.tracked < policy.earn_req_per_block ? previous : current;

  return !!earnedBlock && !!current && earnedBlock.tracked === policy.earn_req_per_block && !current.break_taken;
};
// We are ENFORCING that any policy we evaluate MUST belong to any of the 7 known rules.
// This means that any unforeseen combination that doesn't EXACTLY match the requirements
// for any policy will be seen as invalid.
// This is because we need to make sure that the received policy is exactly what we planned it
// to be and the UI is ready to handle it without triggering unexpected results.
// Any new rules will have to be added to this method so that we can understand them as valid.
//
// No restriction (Rule 1)
// * restriction_type = 0
// * earn_req_per_session = null
// * earn_req_per_block = null
// * block_duration = null
//
// Allowed once per session without minimum (Rule 2)
// * restriction_type = 1
// * earn_req_per_session = null
// * earn_req_per_block = null
// * block_duration = null
//
// Allowed once per session with minimum of W (Rule 3)
// * restriction_type = 1
// * earn_req_per_session = Integer (W)
// * earn_req_per_block = null
// * block_duration = null
//
// Allowed within every Y seconds without minimum (Rule 4)
// * restriction_type = 2
// * earn_req_per_session = null
// * earn_req_per_block = null
// * block_duration = Integer (Y)
//
// Allowed within every Y seconds with a min of W (Rule 5)
// * restriction_type = 2
// * earn_req_per_session = Integer W
// * earn_req_per_block = null
// * block_duration = Integer Y
//
// Allowed after every Y seconds (Rule 6)
// * restriction_type = 3
// * earn_req_per_session = null
// * earn_req_per_block = Integer Y
// * block_duration = Integer Y
//
// Allowed after every Y seconds and a min of W (Rule 7)
// * restriction_type = 3
// * earn_req_per_session = Integer W
// * earn_req_per_block = Integer Y
// * block_duration = Integer Y
const appliedRule = (policy) => {
  if (
    !policy.earn_req_per_block &&
    !policy.earn_req_per_session &&
    !policy.block_duration &&
    policy.restriction_type === 0
  ) {
    return 1;
  }
  if (
    !policy.earn_req_per_block &&
    !policy.earn_req_per_session &&
    !policy.block_duration &&
    policy.restriction_type === 1
  ) {
    return 2;
  }
  if (
    !policy.earn_req_per_block &&
    policy.earn_req_per_session &&
    !policy.block_duration &&
    policy.restriction_type === 1
  ) {
    return 3;
  }
  if (
    !policy.earn_req_per_block &&
    !policy.earn_req_per_session &&
    policy.block_duration &&
    policy.restriction_type === 2
  ) {
    return 4;
  }
  if (
    !policy.earn_req_per_block &&
    policy.earn_req_per_session &&
    policy.block_duration &&
    policy.restriction_type === 2
  ) {
    return 5;
  }
  if (
    policy.earn_req_per_block &&
    policy.block_duration &&
    policy.earn_req_per_block === policy.block_duration &&
    !policy.earn_req_per_session &&
    policy.restriction_type === 3
  ) {
    return 6;
  }
  if (
    policy.earn_req_per_block &&
    policy.block_duration &&
    policy.earn_req_per_block === policy.block_duration &&
    policy.earn_req_per_session &&
    policy.restriction_type === 3
  ) {
    return 7;
  }

  return 0;
};

export const hasTrackedTime = (session) => !!session && !!session.tracked && !!session.work_session_blocks.length;

export const isValidPolicy = (policy) => !!appliedRule(policy);

// Requirements for taking a break:
// * Rule 1: No restriction.
// * Rule 2: Earn a break once per session.
// * Rule 3: Earn a break once per session with a minimum earn_req_per_session.
// * Rule 4: Earn a break every block_duration time.
// * Rule 5: Earn a break every block_duration time with a minimum earn_req_per_session.
// * Rule 6: Earn a break every earn_req_per_block. Time tracked accumulates. Unused breaks expire after taking a break.
// * Rule 7: Earn a break every earn_req_per_block with a minimum of earn_req_per_session. Time tracked accumulates. Unused breaks expire after taking a break.

export const canTakeBreak = (policy, session) => {
  if (isValidPolicy(policy)) {
    const rule = appliedRule(policy);

    switch (rule) {
      case 1:
        return true;
      case 2:
        return hasTrackedTime(session) && !hasTakenAnyBreak(session);
      case 3:
        return hasTrackedTime(session) && meetsMinSessionTime(session, policy) && !hasTakenAnyBreak(session);
      case 4:
        return hasTrackedTime(session) && meetsWithinBlockRequirements(session, policy);
      case 5:
        return (
          hasTrackedTime(session) &&
          meetsMinSessionTime(session, policy) &&
          meetsWithinBlockRequirements(session, policy)
        );
      case 6:
        return (
          hasTrackedTime(session) &&
          isValidBlock(session, policy) &&
          meetsAccummulatingBlockRequirements(session, policy)
        );
      case 7:
        return (
          hasTrackedTime(session) &&
          meetsMinSessionTime(session, policy) &&
          isValidBlock(session, policy) &&
          meetsAccummulatingBlockRequirements(session, policy)
        );
      default:
        return false;
    }
  }
};
